// <i80-flow>: our own component (not in Helios), made for i80 (zPlan uses it). A flow
// diagram: a palette of block templates on the left, a canvas of nodes and the edges
// between them on the right. Blocks drag (or click) onto the canvas to make nodes, nodes
// drag to move, and dragging from a node's port to another node draws an edge.
//
// Like the timeline, it only draws and reports. The state is the child elements the
// server renders (<i80-flow-node>, <i80-flow-edge>); every change fires a cancellable
// event (i80-flow-add/move/connect/delete) that the page or HTMX answers. If nothing
// cancels it, the component writes the change into the markup itself, so it also works
// as a standalone editor.
import { I80Element, define, h, nextId } from "../base.js";
import { icon } from "../icons.js";
import css from "./flow.css";

// One accent colour, meaningful rather than decorative: it marks a node and the connector
// into it. The family is chosen on the host (accent-color); everything else stays calm.
const ACCENTS = { red: "500", amber: "600", blue: "500", purple: "500", green: "500" };

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

function ensureStyles() {
  if (document.querySelector("style[data-i80-flow]")) return;
  const style = document.createElement("style");
  style.setAttribute("data-i80-flow", "");
  style.textContent = css;
  document.head.append(style);
}

const SVGNS = "http://www.w3.org/2000/svg";
function svg(tag, attrs = {}) {
  const el = document.createElementNS(SVGNS, tag);
  for (const [k, v] of Object.entries(attrs)) if (v !== null && v !== undefined && v !== false) el.setAttribute(k, v);
  return el;
}

/** The point on a rectangle's border along the line from its centre towards (tx, ty). */
function border(rect, tx, ty) {
  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h / 2;
  const dx = tx - cx;
  const dy = ty - cy;
  if (!dx && !dy) return { x: cx, y: cy };
  const sx = dx ? rect.w / 2 / Math.abs(dx) : Infinity;
  const sy = dy ? rect.h / 2 / Math.abs(dy) : Infinity;
  const s = Math.min(sx, sy);
  return { x: cx + dx * s, y: cy + dy * s };
}

/**
 * <i80-flow label="Fra vagt til løn" accent-color="red" grid="16" height="480">
 *   <i80-flow-palette label="Blocks">
 *     <i80-flow-block icon="clock">Vagt</i80-flow-block>
 *     <i80-flow-block icon="plus-circle">Tillæg</i80-flow-block>
 *     <i80-flow-block icon="alert-triangle" accent>Brud</i80-flow-block>
 *   </i80-flow-palette>
 *   <i80-flow-node id="n1" icon="clock" sub="16–23" x="64" y="32">Aftenvagt</i80-flow-node>
 *   <i80-flow-node id="n2" icon="moon" sub="9 t" x="64" y="176"
 *     detail="Reglen kræver 11 timer.">Hvile</i80-flow-node>
 *   <i80-flow-node id="n3" icon="alert-triangle" accent x="64" y="320"
 *     detail="Kun 9 timer — 2 t for lidt.">Brud</i80-flow-node>
 *   <i80-flow-edge from="n1" to="n2"></i80-flow-edge>
 *   <i80-flow-edge from="n2" to="n3"></i80-flow-edge>
 * </i80-flow>
 */
export class I80Flow extends I80Element {
  static get observedAttributes() {
    return ["label", "label-palette", "label-instructions", "label-empty", "label-info", "label-close", "grid", "height", "readonly", "accent-color"];
  }

  get accent() {
    const fam = this.getAttribute("accent-color");
    return ACCENTS[fam] ? fam : "red";
  }

  setup() {
    ensureStyles();
    this.querySelector(':scope > [data-i80-part="flow"]')?.remove();
    this._root = h("div", { class: "i80-flow", "data-i80-part": "flow" });
    this.append(this._root);
    this._nodeEls = new Map(); // rendered button -> source <i80-flow-node>
    // All pointer, click and key handling is on _root: the palette and the canvas are both
    // inside it, so one set of listeners covers a drag that starts in either, and pointer
    // capture on _root keeps the events coming here for the whole drag.
    this._root.addEventListener("pointerdown", (e) => this._down(e));
    this._root.addEventListener("pointermove", (e) => this._move(e));
    this._root.addEventListener("pointerup", (e) => this._up(e));
    this._root.addEventListener("pointercancel", () => this._cancelDrag());
    this._root.addEventListener("click", (e) => this._click(e));
    this._root.addEventListener("keydown", (e) => this._key(e));
    this._observer = new MutationObserver((records) => {
      const relevant = (r) => (r.target === this ? [...r.addedNodes, ...r.removedNodes].some((n) => n !== this._root) : !this._root.contains(r.target));
      if (!this._drag && records.some(relevant)) this.update();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._observer?.disconnect();
  }

  get readonly() {
    return this.flag("readonly");
  }

  get grid() {
    const n = parseInt(this.getAttribute("grid") ?? "16", 10);
    return Number.isFinite(n) && n > 0 ? n : 16;
  }

  _snap(v) {
    const g = this.grid;
    return Math.round(v / g) * g;
  }

  get blocks() {
    const palette = this.querySelector(":scope > i80-flow-palette");
    return palette ? [...palette.querySelectorAll(":scope > i80-flow-block")] : [];
  }

  get nodes() {
    return [...this.querySelectorAll(":scope > i80-flow-node")];
  }

  get edges() {
    return [...this.querySelectorAll(":scope > i80-flow-edge")];
  }

  nodeById(id) {
    return this.nodes.find((n) => n.getAttribute("id") === id) ?? null;
  }

  // --- rendering ---

  update() {
    this._observer?.disconnect();
    try {
      this._render();
    } finally {
      if (this.isConnected) this._observer.observe(this, { childList: true, subtree: true, attributes: true, characterData: true });
    }
  }

  _render() {
    // Keep focus across a redraw: remember which source node was focused, restore after.
    const active = this._root.contains(document.activeElement) ? document.activeElement.closest?.(".i80-flow__node") : null;
    const focused = active ? this._nodeEls.get(active) : null;
    const instructionsId = `${this.id || "i80-flow"}-help`;
    this._nodeEls = new Map();

    const fam = this.accent;
    this._root.style.setProperty("--i80-flow-accent-tint", `var(--i80-color-palette-${fam}-100)`);
    this._root.style.setProperty("--i80-flow-accent-ink", `var(--i80-color-palette-${fam}-${ACCENTS[fam]})`);
    this._root.style.setProperty("--i80-flow-accent-edge", `var(--i80-color-palette-${fam}-300)`);

    const palette = this.readonly ? null : h("div", { class: "i80-flow__palette", role: "group", "aria-label": this.getAttribute("label-palette") ?? "Blocks" },
      h("p", { class: "i80-flow__palette-title", "aria-hidden": "true" }, this.getAttribute("label-palette") ?? "Blocks"),
      this.blocks.map((b) => this._blockButton(b)));

    const surface = h("div", { class: "i80-flow__surface" });
    const edgeLayer = svg("svg", { class: "i80-flow__edges" });
    const nodeEls = this.nodes.map((n) => this._nodeButton(n, instructionsId));
    surface.append(edgeLayer, ...nodeEls);
    if (!this.nodes.length) surface.append(h("div", { class: "i80-flow__empty" }, this.getAttribute("label-empty") ?? "Drag a block here to start."));

    const canvas = h("div", { class: "i80-flow__canvas", tabindex: "-1", role: "group", "aria-label": this.getAttribute("label") ?? "Flow diagram", style: `height:${parseInt(this.getAttribute("height") ?? "480", 10) || 480}px` });
    canvas.style.setProperty("--i80-flow-grid", `${this.grid}px`);
    canvas.append(surface);
    this._canvas = canvas;
    this._surface = surface;
    this._edgeLayer = edgeLayer;

    this._root.replaceChildren(
      h("span", { id: instructionsId, class: "i80-flow__sr" },
        this.getAttribute("label-instructions") ?? "Arrow keys move the block, c starts a connection then Tab to a block and Enter joins them, Delete removes the block."),
      palette,
      canvas);

    // A node's size is only known once it is laid out, so measure now and draw the edges
    // (and grow the surface to hold a node past the canvas edge) in a second pass.
    this._layoutEdges();
    if (focused) [...this._nodeEls].find(([, src]) => src === focused)?.[0].querySelector(".i80-flow__node-card")?.focus();
  }

  _head(src, text) {
    const name = src.getAttribute("icon");
    const sub = src.getAttribute("sub");
    return h("div", { class: "i80-flow__head" },
      name && h("span", { class: "i80-flow__chip" }, icon(name, {})),
      h("span", { class: "i80-flow__text" },
        h("span", { class: "i80-flow__name" }, text),
        sub && h("span", { class: "i80-flow__sub" }, sub)));
  }

  _blockButton(src) {
    const text = src.textContent.trim() || "Block";
    const btn = h("button", {
      type: "button",
      class: ["i80-flow__block", src.hasAttribute("accent") && "i80-flow__block--accent"],
      "data-i80-block": src.getAttribute("type") ?? "",
      "aria-label": `Add ${text}`,
      title: `Add ${text}`,
    }, this._head(src, text));
    btn._src = src;
    return btn;
  }

  /** A node has more to say when it carries an `info` attribute or an inline info template. */
  _hasInfo(src) {
    return src.hasAttribute("info") || !!src.querySelector(":scope > template[data-i80-info]");
  }

  _nodeButton(src, instructionsId) {
    const text = src.textContent.trim() || "Node";
    const detail = src.getAttribute("detail");
    const x = parseFloat(src.getAttribute("x")) || 0;
    const y = parseFloat(src.getAttribute("y")) || 0;
    const id = src.getAttribute("id");
    const movable = !this.readonly && !src.hasAttribute("locked");
    const info = this._hasInfo(src);
    const out = this.edges.filter((e) => e.getAttribute("from") === id).map((e) => this.nodeById(e.getAttribute("to"))).filter(Boolean).map((n) => n.textContent.trim());

    // The card is the button that drags, selects and takes the keyboard. The info button and
    // the port are its siblings in the wrapper — so neither is a button nested inside a button.
    const card = h("button", {
      type: "button",
      class: "i80-flow__node-card",
      "aria-label": [text, src.getAttribute("sub"), detail, out.length && `connects to ${out.join(", ")}`].filter(Boolean).join(", "),
      "aria-pressed": String(src.hasAttribute("selected")),
      "aria-describedby": movable ? instructionsId : null,
      "aria-disabled": movable ? null : "true",
    },
    this._head(src, text),
    detail && h("div", { class: "i80-flow__detail" }, h("span", {}, detail)));

    const node = h("div", {
      class: ["i80-flow__node", src.hasAttribute("accent") && "i80-flow__node--accent", src.hasAttribute("selected") && "i80-flow__node--selected", src.hasAttribute("locked") && "i80-flow__node--locked", info && "i80-flow__node--has-info"],
      style: `left:${x}px;top:${y}px`,
    },
    card,
    info && h("button", {
      type: "button",
      class: "i80-flow__info",
      "data-i80-info": "",
      "aria-label": `${this.getAttribute("label-info") ?? "Details"}: ${text}`,
      title: this.getAttribute("label-info") ?? "Details",
    }, icon("info", { size: "16" })),
    movable && h("span", { class: "i80-flow__port", "data-i80-port": "", "aria-hidden": "true" }));
    this._nodeEls.set(node, src);
    return node;
  }

  /** Measure the rendered nodes, size the surface to fit, and draw the edges between them. */
  _layoutEdges() {
    const rects = new Map();
    let maxX = 0;
    let maxY = 0;
    for (const [btn, src] of this._nodeEls) {
      const r = { x: parseFloat(src.getAttribute("x")) || 0, y: parseFloat(src.getAttribute("y")) || 0, w: btn.offsetWidth, h: btn.offsetHeight };
      rects.set(src.getAttribute("id"), r);
      maxX = Math.max(maxX, r.x + r.w);
      maxY = Math.max(maxY, r.y + r.h);
    }
    this._surface.style.width = `${maxX + 40}px`;
    this._surface.style.height = `${maxY + 40}px`;

    const layer = this._edgeLayer;
    layer.replaceChildren(this._arrowMarker());
    for (const edge of this.edges) {
      const a = rects.get(edge.getAttribute("from"));
      const b = rects.get(edge.getAttribute("to"));
      if (!a || !b) continue;
      const ac = { x: a.x + a.w / 2, y: a.y + a.h / 2 };
      const bc = { x: b.x + b.w / 2, y: b.y + b.h / 2 };
      const p1 = border(a, bc.x, bc.y);
      const p2 = border(b, ac.x, ac.y);
      const d = this._path(p1, p2);
      // The one accent: an edge is accented when it says so, or when it leads into an
      // accented node — so the eye can follow the path to the node that matters.
      const edgeAccent = edge.hasAttribute("accent") || !!this.nodeById(edge.getAttribute("to"))?.hasAttribute("accent");
      const selected = edge.hasAttribute("selected");
      const line = svg("path", { class: ["i80-flow__edge", edgeAccent && "i80-flow__edge--accent", edge.hasAttribute("dashed") && "i80-flow__edge--dashed", selected && "i80-flow__edge--selected"].filter(Boolean).join(" "), d, "marker-end": `url(#i80-flow-arrow${edgeAccent ? "-accent" : ""})` });
      const hit = svg("path", { class: "i80-flow__edge-hit", d });
      hit._edge = edge;
      layer.append(line, hit);
      const label = edge.getAttribute("label");
      if (label) {
        const fo = svg("foreignObject", { x: (p1.x + p2.x) / 2 - 40, y: (p1.y + p2.y) / 2 - 12, width: 80, height: 24, style: "overflow:visible" });
        const span = h("div", { class: "i80-flow__edge-label-wrap", style: "display:flex;justify-content:center" }, h("span", { class: "i80-flow__edge-label" }, label));
        span.firstChild._edge = edge;
        fo.append(span);
        layer.append(fo);
      }
    }
  }

  _arrowMarker() {
    const defs = svg("defs");
    for (const [id, cls] of [["i80-flow-arrow", "i80-flow__edge-arrow"], ["i80-flow-arrow-accent", "i80-flow__edge-arrow i80-flow__edge-arrow--accent"]]) {
      const marker = svg("marker", { id, viewBox: "0 0 10 10", refX: "8", refY: "5", markerWidth: "6", markerHeight: "6", orient: "auto-start-reverse" });
      marker.append(svg("path", { class: cls, d: "M1,1 L9,5 L1,9 z" }));
      defs.append(marker);
    }
    return defs;
  }

  /** A cubic bezier with the control points pushed along the dominant axis — a gentle flow curve. */
  _path(p1, p2) {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const horizontal = Math.abs(dx) > Math.abs(dy);
    const off = Math.min(Math.max((horizontal ? Math.abs(dx) : Math.abs(dy)) * 0.45, 22), 90);
    const c1 = horizontal ? { x: p1.x + Math.sign(dx || 1) * off, y: p1.y } : { x: p1.x, y: p1.y + Math.sign(dy || 1) * off };
    const c2 = horizontal ? { x: p2.x - Math.sign(dx || 1) * off, y: p2.y } : { x: p2.x, y: p2.y - Math.sign(dy || 1) * off };
    return `M${p1.x},${p1.y} C${c1.x},${c1.y} ${c2.x},${c2.y} ${p2.x},${p2.y}`;
  }

  _canvasPoint(e) {
    const r = this._surface.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  // --- pointer: add from palette, move a node, draw an edge ---

  _down(e) {
    if (e.button !== 0 || this.readonly) return;
    if (e.target.closest("[data-i80-info]")) return; // the info button opens on click; never a drag
    const block = e.target.closest(".i80-flow__block");
    // Capture on the element itself, not _root: a capture on a container retargets the
    // click that follows to that container, and the palette/select clicks would be lost.
    if (block) {
      this._drag = { kind: "add", block: block._src, moved: false, x0: e.clientX, y0: e.clientY };
      block.setPointerCapture?.(e.pointerId);
      e.preventDefault();
      return;
    }
    const node = e.target.closest(".i80-flow__node");
    if (!node) return;
    const src = this._nodeEls.get(node);
    if (e.target.closest("[data-i80-port]")) {
      this._drag = { kind: "link", from: src, node };
      node.classList.add("i80-flow__node--linking");
      node.setPointerCapture?.(e.pointerId);
      e.preventDefault();
    } else if (!node.classList.contains("i80-flow__node--locked")) {
      const card = node.querySelector(".i80-flow__node-card");
      this._drag = { kind: "move", node, card, src, x0: e.clientX, y0: e.clientY, ox: parseFloat(src.getAttribute("x")) || 0, oy: parseFloat(src.getAttribute("y")) || 0, moved: false };
      // Capture on the card, not the wrapper: a capture on a container retargets the click
      // that follows to that container, and the select click would be lost.
      card.setPointerCapture?.(e.pointerId);
      card.focus();
      e.preventDefault();
    }
  }

  _move(e) {
    const d = this._drag;
    if (!d) return;
    if (d.kind === "move") {
      if (!d.moved && Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < 4) return;
      d.moved = true;
      d.node.classList.add("i80-flow__node--dragging");
      d.nx = clamp(this._snap(d.ox + (e.clientX - d.x0)), 0, 100000);
      d.ny = clamp(this._snap(d.oy + (e.clientY - d.y0)), 0, 100000);
      d.node.style.left = `${d.nx}px`;
      d.node.style.top = `${d.ny}px`;
      this._layoutEdges();
    } else if (d.kind === "link") {
      this._drawDraft(e, d);
      this._highlightTarget(e, d);
    } else if (d.kind === "add") {
      if (!d.moved && Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < 4) return;
      d.moved = true;
      this._ghost(e, d);
    }
  }

  _ghost(e, d) {
    if (!d.ghost) {
      d.ghost = this._blockButton(d.block);
      d.ghost.classList.add("i80-flow__node", "i80-flow__node--dragging");
      d.ghost.style.position = "fixed";
      d.ghost.style.pointerEvents = "none";
      document.body.append(d.ghost);
    }
    d.ghost.style.left = `${e.clientX + 8}px`;
    d.ghost.style.top = `${e.clientY + 8}px`;
    const over = this._overCanvas(e);
    this._canvas.style.outline = over ? `2px solid var(--i80-color-palette-blue-300)` : "";
  }

  _drawDraft(e, d) {
    const p = this._canvasPoint(e);
    this._edgeLayer.querySelector(".i80-flow__edge-draft")?.remove();
    const btn = [...this._nodeEls].find(([, s]) => s === d.from)?.[0];
    const r = { x: parseFloat(d.from.getAttribute("x")) || 0, y: parseFloat(d.from.getAttribute("y")) || 0, w: btn.offsetWidth, h: btn.offsetHeight };
    const start = border(r, p.x, p.y);
    this._edgeLayer.append(svg("path", { class: "i80-flow__edge-draft", d: `M${start.x},${start.y} L${p.x},${p.y}` }));
  }

  _highlightTarget(e, d) {
    const over = this._nodeUnder(e);
    for (const el of this._root.querySelectorAll(".i80-flow__node--target")) if (el !== over) el.classList.remove("i80-flow__node--target");
    if (over && this._nodeEls.get(over) !== d.from) over.classList.add("i80-flow__node--target");
  }

  _nodeUnder(e) {
    return document.elementsFromPoint(e.clientX, e.clientY).find((el) => el.classList?.contains("i80-flow__node") && this._root.contains(el)) ?? null;
  }

  _overCanvas(e) {
    return document.elementsFromPoint(e.clientX, e.clientY).some((el) => el === this._canvas || this._canvas.contains(el));
  }

  _up(e) {
    const d = this._drag;
    if (!d) return;
    this._drag = null;
    this._edgeLayer?.querySelector(".i80-flow__edge-draft")?.remove();
    this._canvas && (this._canvas.style.outline = "");
    for (const el of this._root.querySelectorAll(".i80-flow__node--target, .i80-flow__node--linking")) el.classList.remove("i80-flow__node--target", "i80-flow__node--linking");

    if (d.kind === "move") {
      d.node.classList.remove("i80-flow__node--dragging");
      if (!d.moved) return;
      this._suppress();
      this._commitMove(d.src, d.nx, d.ny);
    } else if (d.kind === "link") {
      const over = this._nodeUnder(e);
      const to = over && this._nodeEls.get(over);
      if (to && to !== d.from) this._commitConnect(d.from, to);
      else this.update();
    } else if (d.kind === "add") {
      d.ghost?.remove();
      if (!d.moved) return; // a plain click adds via _click instead
      this._suppress();
      if (this._overCanvas(e)) {
        const p = this._canvasPoint(e);
        this._commitAdd(d.block, this._snap(p.x - 48), this._snap(p.y - 18));
      }
    }
  }

  /** Swallow the click that a pointerup synthesises after a real drag. */
  _suppress() {
    this._suppressClick = true;
    setTimeout(() => (this._suppressClick = false));
  }

  _cancelDrag() {
    if (!this._drag) return;
    this._drag.ghost?.remove();
    this._drag = null;
    this.update();
  }

  // --- commits: fire a cancellable event, then write the change unless it is prevented ---

  _commitMove(src, x, y) {
    const previous = { x: parseFloat(src.getAttribute("x")) || 0, y: parseFloat(src.getAttribute("y")) || 0 };
    if (previous.x === x && previous.y === y) return this.update();
    const ok = this.dispatchEvent(new CustomEvent("i80-flow-move", { bubbles: true, cancelable: true, detail: { node: src, id: src.getAttribute("id"), x, y, previous } }));
    if (ok && src.isConnected) {
      src.setAttribute("x", x);
      src.setAttribute("y", y);
    }
    this.update();
  }

  _commitConnect(from, to) {
    const fromId = from.getAttribute("id");
    const toId = to.getAttribute("id");
    if (!fromId || !toId || this.edges.some((e) => e.getAttribute("from") === fromId && e.getAttribute("to") === toId)) return this.update();
    const ok = this.dispatchEvent(new CustomEvent("i80-flow-connect", { bubbles: true, cancelable: true, detail: { from: fromId, to: toId } }));
    if (ok) {
      const edge = document.createElement("i80-flow-edge");
      edge.setAttribute("from", fromId);
      edge.setAttribute("to", toId);
      this.append(edge);
    }
    this.update();
  }

  _commitAdd(block, x, y) {
    const detail = { type: block.getAttribute("type") ?? "", x, y, icon: block.getAttribute("icon"), sub: block.getAttribute("sub"), detail: block.getAttribute("detail"), info: block.getAttribute("info"), accent: block.hasAttribute("accent"), text: block.textContent.trim(), block };
    const ok = this.dispatchEvent(new CustomEvent("i80-flow-add", { bubbles: true, cancelable: true, detail }));
    if (ok) {
      const node = document.createElement("i80-flow-node");
      node.setAttribute("id", nextId("i80-flow-node"));
      for (const a of ["type", "icon", "sub", "detail", "info"]) if (block.getAttribute(a)) node.setAttribute(a, block.getAttribute(a));
      if (block.hasAttribute("accent")) node.setAttribute("accent", "");
      node.setAttribute("x", x);
      node.setAttribute("y", y);
      node.textContent = block.textContent.trim();
      const tpl = block.querySelector(":scope > template[data-i80-info]");
      if (tpl) node.append(tpl.cloneNode(true));
      this.append(node);
    }
    this.update();
  }

  _commitDelete(src, kind) {
    const detail = kind === "node"
      ? { kind, id: src.getAttribute("id"), node: src }
      : { kind, edge: src, from: src.getAttribute("from"), to: src.getAttribute("to") };
    const ok = this.dispatchEvent(new CustomEvent("i80-flow-delete", { bubbles: true, cancelable: true, detail }));
    if (ok) {
      if (kind === "node") {
        const id = src.getAttribute("id");
        for (const e of this.edges) if (e.getAttribute("from") === id || e.getAttribute("to") === id) e.remove();
      }
      src.remove();
    }
    this.update();
  }

  // --- info: fire a cancellable event, then (unless answered) show a built-in modal ---

  _openInfo(src) {
    if (!src) return;
    const text = src.textContent.trim() || "Node";
    // Report first: the page may show its own explanation (e.g. fetched from the server).
    const ok = this.dispatchEvent(new CustomEvent("i80-flow-info", { bubbles: true, cancelable: true, detail: { id: src.getAttribute("id"), node: src, text, info: src.getAttribute("info") } }));
    if (!ok) return;

    const tpl = src.querySelector(":scope > template[data-i80-info]");
    const body = src.getAttribute("info") ?? src.getAttribute("detail") ?? "";
    if (!tpl && !body) return; // nothing to show, and nobody answered

    const modal = h("i80-modal", { heading: text, size: "small" });
    const sub = src.getAttribute("sub");
    if (sub) modal.setAttribute("tagline", sub);
    const iconName = src.getAttribute("icon");
    if (iconName) modal.setAttribute("icon", iconName);
    if (src.hasAttribute("accent")) modal.setAttribute("color", this.accent === "amber" ? "warning" : "critical");

    if (tpl) modal.append(tpl.content.cloneNode(true));
    else modal.append(h("p", { class: "i80-text i80-typography-body-200" }, body));
    modal.append(h("i80-button", { slot: "footer", color: "primary", close: "" }, this.getAttribute("label-close") ?? "Close"));

    document.body.append(modal);
    // The dialog is in the top layer, so where it lives in the DOM doesn't matter. Remove it
    // once it has closed — DialogElement drops the `open` attribute on close.
    const mo = new MutationObserver(() => { if (!modal.hasAttribute("open")) { mo.disconnect(); modal.remove(); } });
    if (typeof modal.show === "function") {
      modal.show();
      mo.observe(modal, { attributes: true, attributeFilter: ["open"] });
    } else {
      modal.remove();
    }
  }

  // --- click and keyboard ---

  _click(e) {
    if (this._suppressClick) return;
    if (e.target.closest("[data-i80-info]")) {
      const node = e.target.closest(".i80-flow__node");
      if (node) this._openInfo(this._nodeEls.get(node));
      return;
    }
    const block = e.target.closest(".i80-flow__block");
    if (block) {
      this._commitAdd(block._src, this._snap(24), this._snap(24 + this.nodes.length * 80));
      return;
    }
    const nodeEl = e.target.closest(".i80-flow__node");
    const hit = e.target.closest(".i80-flow__edge-hit") || e.target.closest(".i80-flow__edge-label");
    if (nodeEl && !e.target.closest("[data-i80-port]")) {
      const src = this._nodeEls.get(nodeEl);
      this._select(src);
      this.dispatchEvent(new CustomEvent("i80-flow-select", { bubbles: true, detail: { kind: "node", id: src.getAttribute("id"), node: src, type: src.getAttribute("type") } }));
    } else if (hit) {
      const edge = hit._edge;
      this._select(edge);
      this.dispatchEvent(new CustomEvent("i80-flow-select", { bubbles: true, detail: { kind: "edge", edge, from: edge.getAttribute("from"), to: edge.getAttribute("to") } }));
    } else if (e.target === this._canvas || e.target.classList.contains("i80-flow__surface") || e.target.classList.contains("i80-flow__empty")) {
      this._select(null);
      this.dispatchEvent(new CustomEvent("i80-flow-select", { bubbles: true, detail: { kind: "canvas" } }));
    }
  }

  /** Mark one node or edge selected in the markup (so a redraw keeps it) and clear the rest. */
  _select(target) {
    for (const el of [...this.nodes, ...this.edges]) el.removeAttribute("selected");
    if (target) target.setAttribute("selected", "");
    this.update();
  }

  _key(e) {
    if (e.key === "Escape" && this._linking) return this._endLinking();
    const nodeEl = e.target.closest?.(".i80-flow__node");
    if (!nodeEl) return;
    const src = this._nodeEls.get(nodeEl);

    if (this._linking && e.key === "Enter") {
      e.preventDefault();
      if (src !== this._linking) this._commitConnect(this._linking, src);
      this._endLinking();
      return;
    }
    if ((e.key === "Delete" || e.key === "Backspace") && !src.hasAttribute("locked") && !this.readonly) {
      e.preventDefault();
      this._commitDelete(src, "node");
      return;
    }
    if ((e.key === "c" || e.key === "C") && !this.readonly && !src.hasAttribute("locked")) {
      e.preventDefault();
      this._linking = src;
      nodeEl.classList.add("i80-flow__node--linking");
      return;
    }
    if (src.hasAttribute("locked") || this.readonly) return;
    const g = this.grid;
    const step = { ArrowLeft: [-g, 0], ArrowRight: [g, 0], ArrowUp: [0, -g], ArrowDown: [0, g] }[e.key];
    if (!step) return;
    e.preventDefault();
    const x = clamp((parseFloat(src.getAttribute("x")) || 0) + step[0], 0, 100000);
    const y = clamp((parseFloat(src.getAttribute("y")) || 0) + step[1], 0, 100000);
    this._commitMove(src, x, y);
  }

  _endLinking() {
    this._linking = null;
    for (const el of this._root.querySelectorAll(".i80-flow__node--linking")) el.classList.remove("i80-flow__node--linking");
  }
}

define("i80-flow", I80Flow);
